import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useDiscardGuard } from '@/shared/hooks/useDiscardGuard';

import { capabilitiesOf } from '../capabilities';
import type { Harness, HarnessType, HarnessWireApi } from '../types';
import {
  isHarnessWireApi,
  WIRE_API_OPTIONS,
  wireApiOrDefault,
} from '../wireApiOptions';
import { validateBaseUrl, validateName } from '../validation';
import { isHarnessError } from '../errors';
import type { HarnessErrorField } from '../errors';
import { HarnessTestConnectionButton } from './HarnessTestConnectionButton';

/** The backend labels an engine "Runs via Claude Code"; the form shows just the engine. */
const engineName = (label: string): string => label.replace(/^Runs via /, '');

export type HarnessFormMode =
  { kind: 'create' } | { kind: 'edit'; entry: Harness };

export interface HarnessFormValues {
  name: string;
  harness_type: string;
  base_url: string;
  /** `undefined` means "leave unchanged" on edit (spec FR-007); required on create. */
  token?: string;
  /** Set only for a type whose capabilities include a wire API (spec 204, spec 240 FR-005). */
  wire_api?: HarnessWireApi;
}

/**
 * Add a harness, or edit an existing one.
 *
 * Mirrors `llm-profiles/components/LlmProfileFormSheet.tsx`'s load-bearing
 * decisions: remount per target, dismissal blocked while a save is in
 * flight, a failed submit keeps the sheet open with the input intact.
 *
 * The token field never shows a previously-saved value (spec FR-006) — on
 * edit it starts blank with placeholder guidance, and submitting it blank
 * means "keep the stored token" (FR-007). On create it is required.
 *
 * The harness type is chosen on create from `types` — the deployment's own list,
 * passed in by `HarnessesSection` (spec 240 FR-003) — and on edit it shows the
 * entry's stored type but cannot be changed (the backend refuses it too). A
 * stored type that is no longer offered still shows, by its own label.
 */
export function HarnessFormSheet({
  mode,
  types,
  open,
  isPending,
  error,
  onSubmit,
  onClearError,
  onCancel,
}: {
  mode: HarnessFormMode;
  /** The types a new harness can be made from, in the deployment's order. */
  types: HarnessType[];
  open: boolean;
  isPending: boolean;
  error: unknown;
  onSubmit: (input: HarnessFormValues) => void;
  onClearError: () => void;
  onCancel: () => void;
}) {
  const editing = mode.kind === 'edit';
  const entry = editing ? mode.entry : undefined;

  const [harnessType, setHarnessType] = useState<string>(
    entry?.harness_type ?? types[0]?.name ?? '',
  );
  const capabilities = capabilitiesOf(harnessType);
  // The label of whatever is selected, even a stored type the deployment no
  // longer lists; its own name when no label is known.
  const labelOf = (name: string): string =>
    engineName(
      types.find((type) => type.name === name)?.label ??
        (entry?.harness_type === name ? entry.label : '') ??
        name,
    );

  // Kept across harness-type toggles so flipping Codex → Claude Code → Codex does
  // not lose the pick; it is rendered and submitted only for a type with a wire API.
  const initialWireApi = wireApiOrDefault(entry?.wire_api);
  const [wireApi, setWireApi] = useState<HarnessWireApi>(initialWireApi);

  const [name, setName] = useState(entry?.name ?? '');
  const [baseUrl, setBaseUrl] = useState(
    entry?.base_url ?? capabilities.defaultBaseUrl ?? '',
  );
  // Whether `baseUrl` has ever diverged from "whatever the current
  // harness type's default is" — once true, switching harness type never
  // overwrites it again (spec 160 FR-002: pre-fill only when empty/untouched).
  const [baseUrlTouched, setBaseUrlTouched] = useState(false);
  const [token, setToken] = useState('');
  const [tokenVisible, setTokenVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const harnessTypeId = useId();
  const wireApiId = useId();
  const nameId = useId();
  const nameErrorId = useId();
  const baseUrlId = useId();
  const baseUrlErrorId = useId();
  const tokenId = useId();
  const tokenErrorId = useId();

  const serverError = isHarnessError(error) ? error : undefined;
  const genericError =
    error && !serverError ? 'The request failed. Please try again.' : undefined;

  const localNameError = validateName(name);
  const localBaseUrlError = validateBaseUrl(baseUrl);
  const localTokenError =
    !editing && token.trim() === '' ? 'Enter a token.' : null;

  function messageFor(field: HarnessErrorField): string | undefined {
    if (serverError?.field === field) return serverError.message;
    if (field === 'form') return genericError;
    if (field === 'name')
      return submitted ? (localNameError ?? undefined) : undefined;
    if (field === 'base_url')
      return submitted ? (localBaseUrlError ?? undefined) : undefined;
    if (field === 'token')
      return submitted ? (localTokenError ?? undefined) : undefined;
    return undefined;
  }

  const canSubmit = name.trim().length > 0 && !isPending;
  // Fields "populated" means whatever the connection needs to actually work:
  // a name and a base URL always, plus a token — on create because nothing
  // is saved yet to test without one, on edit because typing a fresh one is
  // itself a reason to want to try the connection right away, even if
  // something else on the form is momentarily incomplete.
  const canTest =
    (name.trim().length > 0 && baseUrl.trim().length > 0) ||
    token.trim().length > 0;

  // What Test should actually check. The rule above deliberately enables the
  // button the moment a fresh token is typed — "typing a fresh one is itself a
  // reason to want to try the connection right away" — but the check used to run
  // against the *stored* pair, so it answered a question nobody asked and a
  // stale token looked like a rejection of the new one. Sending the edited
  // fields makes the button test what the admin is looking at.
  //
  // `undefined` when nothing was edited, so an untouched form keeps testing the
  // saved harness — and keeps persisting that verdict to the badge, which an
  // overridden check deliberately does not do.
  const editedBaseUrl = baseUrl.trim() !== (entry?.base_url ?? '').trim();
  const editedToken = token.trim().length > 0;
  const pendingCredentials =
    editedBaseUrl || editedToken
      ? {
          ...(editedBaseUrl ? { base_url: baseUrl.trim() } : {}),
          ...(editedToken ? { token: token.trim() } : {}),
        }
      : undefined;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;
    // The mirrored rules inform; a submit attempt that disagrees with one
    // (base URL shape, a missing token on create) never reaches the server —
    // same "inform, don't gate the button" split `LlmProfileFormSheet` uses,
    // so the message is always reachable by clicking Add rather than sitting
    // behind a disabled button no click can trigger.
    if (localBaseUrlError !== null || localTokenError !== null) return;
    onSubmit({
      name,
      harness_type: harnessType,
      base_url: baseUrl.trim(),
      token: token.trim() === '' ? undefined : token.trim(),
      ...(capabilities.hasWireApi ? { wire_api: wireApi } : {}),
    });
  }

  // The token never leaves this component; only whether one was typed counts.
  const dirty =
    name !== (entry?.name ?? '') ||
    baseUrl !== (entry?.base_url ?? capabilities.defaultBaseUrl ?? '') ||
    token !== '' ||
    (capabilities.hasWireApi && wireApi !== initialWireApi);
  // A successful save closes through `open`, so it never asks.
  const { requestClose, dialog } = useDiscardGuard(dirty, onCancel);

  const nameMessage = messageFor('name');
  const baseUrlMessage = messageFor('base_url');
  const tokenMessage = messageFor('token');
  const formMessage = messageFor('form');

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (isPending) return;
          if (!next) requestClose();
        }}
      >
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {editing ? `Edit ${entry?.name}` : 'Add a harness'}
            </SheetTitle>
            <SheetDescription>
              A harness owns the base URL and credential a workflow reaches the
              model through.
            </SheetDescription>
          </SheetHeader>

          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <SheetBody className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={harnessTypeId}>Harness engine</Label>
                  <RequiredMark />
                </div>
                <Select
                  value={harnessType}
                  onValueChange={(next) => {
                    if (!next) return;
                    setHarnessType(next);
                    const defaultUrl = capabilitiesOf(next).defaultBaseUrl;
                    // Pre-fill only when the admin hasn't typed anything —
                    // never overwrite a value they already entered (spec 160
                    // FR-002).
                    if (defaultUrl && !baseUrlTouched) {
                      setBaseUrl(defaultUrl);
                    }
                  }}
                  disabled={isPending || editing}
                >
                  <SelectTrigger id={harnessTypeId} aria-label="Harness engine">
                    <SelectValue>
                      {(value: string) => labelOf(value) || value}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((type) => (
                      <SelectItem key={type.name} value={type.name}>
                        {engineName(type.label) || type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {capabilities.hasWireApi ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={wireApiId}>Wire API</Label>
                  <Select
                    value={wireApi}
                    onValueChange={(next) => {
                      if (isHarnessWireApi(next)) setWireApi(next);
                    }}
                    disabled={isPending}
                  >
                    <SelectTrigger id={wireApiId} aria-label="Wire API">
                      <SelectValue>
                        {(value: string) =>
                          WIRE_API_OPTIONS.find(
                            (option) => option.value === value,
                          )?.label ?? value
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {WIRE_API_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground text-xs">
                    Choose Chat Completions if your gateway does not support the
                    Responses API.
                  </p>
                </div>
              ) : null}

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={nameId}>Harness name</Label>
                  <RequiredMark />
                </div>
                <Input
                  id={nameId}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    if (serverError?.field === 'name') onClearError();
                  }}
                  disabled={isPending}
                  placeholder={capabilities.namePlaceholder ?? 'My harness'}
                  autoFocus
                  aria-invalid={nameMessage ? true : undefined}
                  aria-describedby={nameMessage ? nameErrorId : undefined}
                />
                {nameMessage ? (
                  <p id={nameErrorId} className="text-destructive text-xs">
                    {nameMessage}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={baseUrlId}>Base URL</Label>
                  <RequiredMark />
                </div>
                <Input
                  id={baseUrlId}
                  value={baseUrl}
                  onChange={(event) => {
                    setBaseUrl(event.target.value);
                    setBaseUrlTouched(true);
                    if (serverError?.field === 'base_url') onClearError();
                  }}
                  disabled={isPending}
                  placeholder="https://"
                  className="font-mono"
                  aria-invalid={baseUrlMessage ? true : undefined}
                  aria-describedby={baseUrlMessage ? baseUrlErrorId : undefined}
                />
                {baseUrlMessage ? (
                  <p id={baseUrlErrorId} className="text-destructive text-xs">
                    {baseUrlMessage}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={tokenId}>API Token</Label>
                  {editing ? null : <RequiredMark />}
                </div>
                <div className="flex gap-1.5">
                  <Input
                    id={tokenId}
                    type={tokenVisible ? 'text' : 'password'}
                    value={token}
                    onChange={(event) => {
                      setToken(event.target.value);
                      if (serverError?.field === 'token') onClearError();
                    }}
                    disabled={isPending}
                    placeholder={
                      editing
                        ? '•••• leave blank to keep unchanged'
                        : 'sk-ant-…'
                    }
                    autoComplete="off"
                    aria-invalid={tokenMessage ? true : undefined}
                    aria-describedby={tokenMessage ? tokenErrorId : undefined}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isPending}
                    onClick={() => {
                      setTokenVisible((visible) => !visible);
                    }}
                  >
                    {tokenVisible ? (
                      <EyeOffIcon aria-hidden="true" />
                    ) : (
                      <EyeIcon aria-hidden="true" />
                    )}
                    {tokenVisible ? 'Hide' : 'Show'}
                  </Button>
                </div>
                {tokenMessage ? (
                  <p id={tokenErrorId} className="text-destructive text-xs">
                    {tokenMessage}
                  </p>
                ) : null}
              </div>

              {mode.kind === 'edit' ? (
                <HarnessTestConnectionButton
                  id={mode.entry.id}
                  overrides={pendingCredentials}
                  disabled={!canTest}
                />
              ) : (
                // Nothing exists to test yet. Editing can check unsaved values
                // (they ride along as overrides), but the endpoint is addressed by
                // harness id — there is no id before the first save, so on create
                // this stays disabled until the connection is saved.
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled
                  title="Save the connection first"
                  className="self-start"
                >
                  Test connection
                </Button>
              )}

              {formMessage ? (
                <p role="alert" className="text-destructive text-sm">
                  {formMessage}
                </p>
              ) : null}
            </SheetBody>

            <SheetFooter>
              <SheetClose
                render={<Button type="button" variant="outline" />}
                disabled={isPending}
              >
                Cancel
              </SheetClose>
              <Button type="submit" disabled={!canSubmit}>
                {isPending ? 'Saving…' : editing ? 'Save' : 'Add'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {dialog}
    </>
  );
}
